/**
 * Shared filter/sort logic for the Mortgage Monitor table. Used by both
 * MortgageMonitorPage (filter bar + sortable headers) and
 * useMortgageMonitorAgentTools (FutureBot set_monitor_filters tool) so the
 * bot and the UI always agree on what "filtered" means.
 */

// Arive mortgage_type values pass through as-is except spelling fixes.
export const LOAN_TYPE_LABELS = { NonQM: 'Non-QM' };
export const fmtLoanType = (t) => (t ? LOAN_TYPE_LABELS[t] || t : '—');

export const DEFAULT_MONITOR_FILTERS = {
  loanTypes: [],       // display labels ('Conventional', 'FHA', 'VA', 'USDA', 'Non-QM', …)
  termYears: null,     // whole years (30, 20, 15, …)
  minSavings: null,    // $/mo
  minBalance: null,
  maxBalance: null,
  minEquityPct: null,
  minScore: null,
};

export const MONITOR_SORT_KEYS = [
  'borrower', 'funded_date', 'mortgage_type', 'loan_term', 'rate_delta_bps',
  'est_current_balance', 'est_monthly_savings', 'est_equity_pct', 'score', 'owner',
];

export const borrowerName = (o) =>
  [o.borrower_first_name, o.borrower_last_name].filter(Boolean).join(' ') || o.borrower_email || 'Borrower';

const SORT_ACCESSORS = {
  borrower: (o) => borrowerName(o).toLowerCase(),
  funded_date: (o) => o.funded_date || null,
  mortgage_type: (o) => (o.mortgage_type ? fmtLoanType(o.mortgage_type).toLowerCase() : null),
  loan_term: (o) => (Number(o.loan_term) > 0 ? Number(o.loan_term) : null),
  rate_delta_bps: (o) => (Number.isFinite(Number(o.rate_delta_bps)) ? Number(o.rate_delta_bps) : null),
  est_current_balance: (o) => (Number.isFinite(Number(o.est_current_balance)) ? Number(o.est_current_balance) : null),
  est_monthly_savings: (o) => (Number.isFinite(Number(o.est_monthly_savings)) ? Number(o.est_monthly_savings) : null),
  est_equity_pct: (o) => (o.est_equity_pct === null || o.est_equity_pct === undefined ? null : Number(o.est_equity_pct)),
  score: (o) => (Number.isFinite(Number(o.score)) ? Number(o.score) : null),
  owner: (o) => (o.owner_name || o.owner_email || '').toLowerCase() || null,
};

/** Sort opportunities by column key; nulls always sink to the bottom. */
export function sortOpportunities(rows, sortKey, sortDir) {
  const accessor = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.score;
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === 'string' || typeof bv === 'string') return String(av).localeCompare(String(bv)) * dir;
    if (av === bv) return 0;
    return (av - bv) * dir;
  });
}

/** Apply the filter bar to one opportunity row. */
export function matchesMonitorFilters(o, filters) {
  if (!filters) return true;
  if (filters.loanTypes?.length && !filters.loanTypes.includes(fmtLoanType(o.mortgage_type))) return false;
  if (filters.termYears !== null && filters.termYears !== undefined) {
    const years = Math.round((Number(o.loan_term) || 0) / 12);
    if (years !== Number(filters.termYears)) return false;
  }
  if (filters.minSavings != null && !(Number(o.est_monthly_savings) >= Number(filters.minSavings))) return false;
  if (filters.minBalance != null && !(Number(o.est_current_balance) >= Number(filters.minBalance))) return false;
  if (filters.maxBalance != null && !(Number(o.est_current_balance) <= Number(filters.maxBalance))) return false;
  if (filters.minEquityPct != null && !(Number(o.est_equity_pct) >= Number(filters.minEquityPct))) return false;
  if (filters.minScore != null && !(Number(o.score) >= Number(filters.minScore))) return false;
  return true;
}

/** Human-readable description of the active filters (for FutureBot context). */
export function describeMonitorFilters(filters) {
  if (!filters) return 'none';
  const parts = [];
  if (filters.loanTypes?.length) parts.push(`types: ${filters.loanTypes.join('/')}`);
  if (filters.termYears != null) parts.push(`${filters.termYears}yr term`);
  if (filters.minSavings != null) parts.push(`savings ≥ $${filters.minSavings}/mo`);
  if (filters.minBalance != null) parts.push(`balance ≥ $${filters.minBalance}`);
  if (filters.maxBalance != null) parts.push(`balance ≤ $${filters.maxBalance}`);
  if (filters.minEquityPct != null) parts.push(`equity ≥ ${filters.minEquityPct}%`);
  if (filters.minScore != null) parts.push(`score ≥ ${filters.minScore}`);
  return parts.length ? parts.join(', ') : 'none';
}
